import http from "@/services/http";
import {
  ContractListParams,
  ContractListItemDto,
  ContractResponseDto,
  CreateContractDto,
  UpdateContractDto,
  ApproveContractDto,
  CancelContractDto,
  ContractDynamicFieldDto,
  CreateDynamicFieldDto,
} from "@/models/contract.model";
import {
  CreateDeferredDownPaymentDto,
  DeferredDownPaymentOverdueReportDto,
  DeferredDownPaymentResponseDto,
  PayDeferredDownPaymentDto,
  UpdateDeferredDownPaymentDto,
} from "@/models/deferredDownPayment.model";

export interface ContractListResponse {
  items: ContractListItemDto[];
  total: number;
}

class ContractService {
  // ==================== CONTRACTS ====================

  async listContracts(params: ContractListParams = {}): Promise<ContractListResponse> {
    const query = new URLSearchParams();
    const searchTerm = params.search?.trim();
    if (searchTerm) {
      query.set("search", searchTerm);
    }
    if (params.status !== null && params.status !== undefined) {
      query.set("status", String(params.status));
    }
    if (params.page) query.set("page", String(params.page));
    if (params.pageSize) query.set("pageSize", String(params.pageSize));
    const qs = query.toString();

    const response = await http.get<
      ContractListItemDto[] | { items?: ContractListItemDto[]; total?: number; totalItems?: number }
    >(`/contracts${qs ? `?${qs}` : ""}`);

    const normalizeItems = (items: ContractListItemDto[]) =>
      items.map((item: any) => ({
        ...item,
        contactName:
          item?.contactName ||
          item?.contact?.fullName ||
          [item?.contact?.firstName, item?.contact?.lastName].filter(Boolean).join(" "),
      }));

    if (Array.isArray(response)) {
      const items = normalizeItems(response);
      return { items, total: items.length };
    }

    const items = normalizeItems(Array.isArray(response?.items) ? response.items : []);
    const totalFromResponse =
      typeof response?.total === "number"
        ? response.total
        : typeof response?.totalItems === "number"
          ? response.totalItems
          : undefined;
    const total = totalFromResponse ?? items.length;
    return { items, total };
  }

  async getContractById(contractId: string): Promise<ContractResponseDto> {
    return http.get<ContractResponseDto>(`/contracts/${contractId}`);
  }

  async assignClientUser(contractId: string, clientUserId: string | null): Promise<void> {
    await http.put<void>(`/contracts/${contractId}/client`, { clientUserId });
  }

  async createContract(data: CreateContractDto): Promise<ContractResponseDto> {
    return http.post<ContractResponseDto>("/contracts", data);
  }

  async updateContract(contractId: string, data: UpdateContractDto): Promise<ContractResponseDto> {
    return http.put<ContractResponseDto>(`/contracts/${contractId}`, data);
  }

  async approveContract(contractId: string, data: ApproveContractDto): Promise<ContractResponseDto> {
    return http.post<ContractResponseDto>(`/contracts/${contractId}/approve`, data);
  }

  async cancelContract(contractId: string, data: CancelContractDto): Promise<ContractResponseDto> {
    return http.post<ContractResponseDto>(`/contracts/${contractId}/cancel`, data);
  }

  // ==================== DEFERRED DOWN PAYMENTS ====================

  async listDeferredDownPayments(contractId: string): Promise<DeferredDownPaymentResponseDto[]> {
    const query = new URLSearchParams();
    query.set("contractId", contractId);
    return http.get<DeferredDownPaymentResponseDto[]>(`/deferred-down-payments?${query.toString()}`);
  }

  async createDeferredDownPayment(data: CreateDeferredDownPaymentDto): Promise<DeferredDownPaymentResponseDto> {
    return http.post<DeferredDownPaymentResponseDto>("/deferred-down-payments", data);
  }

  async updateDeferredDownPayment(
    deferredDownPaymentId: string,
    data: UpdateDeferredDownPaymentDto,
  ): Promise<DeferredDownPaymentResponseDto> {
    return http.put<DeferredDownPaymentResponseDto>(`/deferred-down-payments/${deferredDownPaymentId}`, data);
  }

  async deleteDeferredDownPayment(deferredDownPaymentId: string): Promise<void> {
    return http.delete<void>(`/deferred-down-payments/${deferredDownPaymentId}`);
  }

  async payDeferredDownPayment(
    deferredDownPaymentId: string,
    data: PayDeferredDownPaymentDto,
  ): Promise<DeferredDownPaymentResponseDto> {
    return http.post<DeferredDownPaymentResponseDto>(`/deferred-down-payments/${deferredDownPaymentId}/pay`, data);
  }

  async getDeferredDownPaymentOverdueReport(params: { asOf?: string } = {}): Promise<DeferredDownPaymentOverdueReportDto> {
    const query = new URLSearchParams();
    if (params.asOf) query.set("asOf", params.asOf);
    const qs = query.toString();
    return http.get<DeferredDownPaymentOverdueReportDto>(`/deferred-down-payments/overdue-report${qs ? `?${qs}` : ""}`);
  }

  // ==================== DYNAMIC FIELDS ====================

  async getDynamicFields(): Promise<ContractDynamicFieldDto[]> {
    return http.get<ContractDynamicFieldDto[]>("/contract-dynamic-fields");
  }

  async createDynamicField(data: CreateDynamicFieldDto): Promise<ContractDynamicFieldDto> {
    return http.post<ContractDynamicFieldDto>("/contract-dynamic-fields", data);
  }

  async deleteDynamicField(fieldId: string): Promise<void> {
    return http.delete(`/contract-dynamic-fields/${fieldId}`);
  }
}

export default new ContractService();
